import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { authService } from '@uniqcamp/shared';
import { canAccessPrincipalFeatures } from '../utils/roles';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import SchoolIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { DispersalsResponse, StudentsResponse } from '../types';

const KPI_CARD = ({
  label,
  value,
  icon,
  gradient,
  loading,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  gradient: string;
  loading?: boolean;
}) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      height: '100%',
      border: '1px solid',
      borderColor: 'divider',
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: 2 },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <Box>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
        {loading ? (
          <Skeleton width={64} height={40} sx={{ mt: 0.5 }} />
        ) : (
          <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>
            {value}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          width: 52,
          height: 52,
          background: gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
        }}
      >
        {icon}
      </Box>
    </Box>
  </Paper>
);

const Dashboard: React.FC = () => {
  const user = authService.getStoredUser();
  const navigate = useNavigate();
  const isPrincipal = user && canAccessPrincipalFeatures(user.role);
  const { canView, loading: featureLoading } = useFeatureAccess('dashboard');
  const today = new Date().toISOString().slice(0, 10);

  React.useEffect(() => {
    if (featureLoading) return;
    if (!isPrincipal && !canView) navigate('/dispersals', { replace: true });
  }, [isPrincipal, canView, featureLoading, navigate]);

  const { data: dispersalsData, isLoading: dispersalsLoading } = useQuery<DispersalsResponse>(
    ['dispersals', today],
    () => apiClient.get(`/dispersals?date=${today}`).then((r: { data: unknown }) => r.data as DispersalsResponse)
  );
  const { data: studentsData, isLoading: studentsLoading } = useQuery<StudentsResponse>(
    'students-dashboard',
    () => apiClient.get('/students').then((r: { data: unknown }) => r.data as StudentsResponse)
  );

  const dispersals = dispersalsData?.dispersals ?? [];
  const students = studentsData?.students ?? [];
  const pendingCount = dispersals.filter(
    (d) => d.status === 'PENDING' || d.status === 'APPROVED_BY_TEACHER'
  ).length;
  const completedToday = dispersals.filter((d) => d.status === 'COMPLETED').length;

  const kpiCards = [
    {
      label: 'Students',
      value: students.length,
      icon: <PeopleIcon sx={{ fontSize: 28 }} />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      loading: studentsLoading,
    },
    {
      label: "Today's Dispersals",
      value: dispersals.length,
      icon: <SchoolIcon sx={{ fontSize: 28 }} />,
      gradient: 'linear-gradient(135deg, #4f227d 0%, #7b4da8 100%)',
      loading: dispersalsLoading,
    },
    {
      label: 'Pending / In progress',
      value: pendingCount,
      icon: <PendingActionsIcon sx={{ fontSize: 28 }} />,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      loading: dispersalsLoading,
    },
    {
      label: 'Completed today',
      value: completedToday,
      icon: <NotificationsActiveIcon sx={{ fontSize: 28 }} />,
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      loading: dispersalsLoading,
    },
  ];

  const recentDispersals = dispersals.slice(0, 5);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 2 }}>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Welcome, {user?.name}. {isPrincipal ? "Here's your school overview." : "Here's your overview."}
      </Typography>

      <Grid container spacing={3}>
        {kpiCards.map((kpi, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <KPI_CARD
              label={kpi.label}
              value={kpi.value}
              icon={kpi.icon}
              gradient={kpi.gradient}
              loading={kpi.loading}
            />
          </Grid>
        ))}

        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Today's Dispersals
                </Typography>
                <Button size="small" endIcon={<ChevronRightIcon />} onClick={() => navigate('/dispersals')}>
                  View all
                </Button>
              </Box>
              {dispersalsLoading ? (
                <Box sx={{ py: 2 }}>
                  <Skeleton variant="rectangular" height={120} />
                </Box>
              ) : recentDispersals.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No dispersals for today yet.
                </Typography>
              ) : (
                <List dense disablePadding>
                  {recentDispersals.map((d) => (
                    <ListItem key={d._id} disablePadding sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <SchoolIcon fontSize="small" color="action" />
                      </ListItemIcon>
                      <ListItemText
                        primary={d.studentId?.name ?? 'Student'}
                        secondary={d.gateName ? `Gate: ${d.gateName}` : d.status}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Quick actions
              </Typography>
              <List dense disablePadding>
                <ListItem disablePadding>
                  <Button fullWidth sx={{ justifyContent: 'flex-start', py: 1.5 }} onClick={() => navigate('/dispersals')}>
                    <SchoolIcon sx={{ mr: 1.5 }} /> Dispersals
                  </Button>
                </ListItem>
                <ListItem disablePadding>
                  <Button fullWidth sx={{ justifyContent: 'flex-start', py: 1.5 }} onClick={() => navigate('/students')}>
                    <PeopleIcon sx={{ mr: 1.5 }} /> Students
                  </Button>
                </ListItem>
                <ListItem disablePadding>
                  <Button fullWidth sx={{ justifyContent: 'flex-start', py: 1.5 }} onClick={() => navigate('/notifications')}>
                    <NotificationsActiveIcon sx={{ mr: 1.5 }} /> Notifications
                  </Button>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
